// src/pages/admin/products/AllProducts.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  Package,
  RefreshCw,
  ChevronDown,
  ShieldAlert
} from 'lucide-react'
import ProductService from '../../../services/productService'
import CategoryService from '../../../services/categoryService'
import UnitsService from '../../../services/unitsService'
import AuthService from '../../../services/authService'
import AddProductModal from '../../../components/modals/AddProductModal'
import ViewProductModal from '../../../components/modals/ViewProductModal'
import EditProductModal from '../../../components/modals/EditProductModal'
import DeleteProductModal from '../../../components/modals/DeleteProductModal'
import InlineLoader from '../../../components/ui/InlineLoader'

const AllProducts = () => {
  // Check user access
  const currentUser = AuthService.getCurrentUser()
  const userRole = currentUser?.role?.toLowerCase()
  
  // Only allow admin, super_admin, and owner roles
  const hasAccess = ['admin', 'super_admin', 'owner'].includes(userRole)

  // State management
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [actionMenuOpen, setActionMenuOpen] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [toast, setToast] = useState(null)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Get product types
  const productTypes = ProductService.getProductTypes()

  // Fetch products from API
  const fetchProducts = async (categoriesData = categories, unitsData = units) => {
    try {
      setLoading(true)
      console.log('🛍️ Fetching products...')
      console.log('📦 Available categories for enrichment:', categoriesData)
      console.log('📏 Available units for enrichment:', unitsData)
      
      const response = await ProductService.getAllProducts()
      
      if (response && response.success) {
        console.log('✅ Products fetched successfully:', response.data)
        const productsData = response.data.products || response.data || []
        console.log('📦 Raw products data:', productsData)
        
        // Populate category and unit details for each product
        const enrichedProducts = productsData.map(product => {
          // Handle case where category/unit might already be objects (populated) or just IDs
          let categoryDetails = null
          let unitDetails = null
          
          // Check if category is already an object (populated) or just an ID
          if (typeof product.category === 'object' && product.category !== null) {
            // Category is already populated
            categoryDetails = product.category
          } else if (typeof product.category === 'string') {
            // Category is just an ID, find it in our fetched categories
            categoryDetails = categoriesData.find(cat => cat._id === product.category)
          }
          
          // Check if unit is already an object (populated) or just an ID
          if (typeof product.unit === 'object' && product.unit !== null) {
            // Unit is already populated
            unitDetails = product.unit
          } else if (typeof product.unit === 'string') {
            // Unit is just an ID, find it in our fetched units
            unitDetails = unitsData.find(unit => unit._id === product.unit)
          }
          
          console.log(`🔍 Product ${product.name}:`)
          console.log(`  - Category:`, product.category)
          console.log(`  - Category type:`, typeof product.category)
          console.log(`  - Found/Using category:`, categoryDetails)
          console.log(`  - Unit:`, product.unit)
          console.log(`  - Unit type:`, typeof product.unit)
          console.log(`  - Found/Using unit:`, unitDetails)
          
          return {
            ...product,
            categoryDetails: categoryDetails || null,
            unitDetails: unitDetails || null,
            // Keep original category/unit for API calls (in case they're IDs)
            category: typeof product.category === 'object' && product.category !== null ? product.category._id : product.category,
            unit: typeof product.unit === 'object' && product.unit !== null ? product.unit._id : product.unit
          }
        })
        
        console.log('✨ Enriched products:', enrichedProducts)
        setProducts(enrichedProducts)
      } else {
        console.log('⚠️ Failed to fetch products:', response?.message)
        setProducts([])
        showToast('Failed to load products', 'error')
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error)
      setProducts([])
      showToast('Error loading products. Please try again.', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch products and categories on component mount
  useEffect(() => {
    const fetchData = async () => {
      console.log('🚀 Starting data fetch sequence...')
      
      try {
        // First, fetch categories
        console.log('📦 Fetching categories...')
        const categoriesResponse = await CategoryService.getAllCategories()
        let categoriesData = []
        if (categoriesResponse && categoriesResponse.success) {
          categoriesData = categoriesResponse.data?.categories || []
          console.log('📦 Categories fetched:', categoriesData.length, categoriesData)
          setCategories(categoriesData)
        }
        
        // Then, fetch units
        console.log('📏 Fetching units...')
        const unitsResponse = await UnitsService.getAllUnits()
        let unitsData = []
        if (unitsResponse && unitsResponse.success) {
          unitsData = unitsResponse.data?.units || []
          console.log('📏 Units fetched:', unitsData.length, unitsData)
          setUnits(unitsData)
        }
        
        // Finally, fetch and enrich products
        console.log('🛍️ Now fetching products with enrichment...')
        await fetchProducts(categoriesData, unitsData)
        
      } catch (error) {
        console.error('❌ Error in fetchData sequence:', error)
      }
    }
    
    fetchData()
  }, [])

  // Re-enrich products when categories or units change
  useEffect(() => {
    if (products.length > 0 && categories.length > 0 && units.length > 0) {
      console.log('🔄 Re-enriching products with updated data...')
      const enrichedProducts = products.map(product => {
        const categoryDetails = categories.find(cat => cat._id === product.category)
        const unitDetails = units.find(unit => unit._id === product.unit)
        
        return {
          ...product,
          categoryDetails: categoryDetails || null,
          unitDetails: unitDetails || null
        }
      })
      
      // Only update if enrichment actually changed something
      const hasChanges = enrichedProducts.some((product, index) => {
        const original = products[index]
        return product.categoryDetails !== original.categoryDetails || 
               product.unitDetails !== original.unitDetails
      })
      
      if (hasChanges) {
        console.log('✨ Products re-enriched with category/unit details')
        setProducts(enrichedProducts)
      }
    }
  }, [categories, units]) // Only depend on categories and units, not products to avoid infinite loop

  // Filter and search products whenever dependencies change
  useEffect(() => {
    let filtered = [...products]

    // Apply search filter
    if (searchTerm) {
      filtered = ProductService.searchProducts(filtered, searchTerm)
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = ProductService.filterByCategory(filtered, categoryFilter)
    }

    // Apply type filter
    if (typeFilter) {
      filtered = ProductService.filterByType(filtered, typeFilter)
    }

    // Apply status filter
    if (statusFilter !== '') {
      const isActive = statusFilter === 'active'
      filtered = ProductService.filterByStatus(filtered, isActive)
    }

    setFilteredProducts(filtered)
  }, [products, searchTerm, categoryFilter, typeFilter, statusFilter])

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  // Handle filter changes
  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value)
  }

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value)
  }

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setCategoryFilter('')
    setTypeFilter('')
    setStatusFilter('')
  }

  // Handle action menu toggle
  const handleActionMenuToggle = (productId) => {
    setActionMenuOpen(actionMenuOpen === productId ? null : productId)
  }

  // Close action menu
  const closeActionMenu = () => {
    setActionMenuOpen(null)
  }

  // Handle view product
  const handleViewProduct = (product) => {
    setSelectedProduct(product)
    setShowViewModal(true)
    closeActionMenu()
  }

  // Handle edit product
  const handleEditProduct = (product) => {
    setSelectedProduct(product)
    setShowEditModal(true)
    closeActionMenu()
  }

  // Handle delete product
  const handleDeleteProduct = (product) => {
    setSelectedProduct(product)
    setShowDeleteModal(true)
    closeActionMenu()
  }

  // Handle status toggle
  const handleStatusToggle = async (product) => {
    const productId = product._id
    const newStatus = !product.isActive

    try {
      setActionLoading(prev => ({ ...prev, [productId]: true }))
      
      const response = await ProductService.toggleProductStatus(productId, newStatus)
      
      if (response && response.success) {
        console.log('✅ Product status toggled successfully')
        setProducts(prev => prev.map(p => 
          p._id === productId ? { ...p, isActive: newStatus } : p
        ))
        showToast(`Product ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success')
      } else {
        console.error('⚠️ Failed to toggle product status:', response?.message)
        showToast('Failed to update product status', 'error')
      }
    } catch (error) {
      console.error('❌ Error toggling product status:', error)
      showToast('Error updating product status', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [productId]: false }))
    }
    
    closeActionMenu()
  }

  // Handle product added
  const handleProductAdded = (newProduct) => {
    console.log('✅ Product added:', newProduct)
    // Enrich the new product with category and unit details
    const enrichedProduct = {
      ...newProduct,
      categoryDetails: categories.find(cat => cat._id === newProduct.category) || null,
      unitDetails: units.find(unit => unit._id === newProduct.unit) || null
    }
    setProducts(prev => [enrichedProduct, ...prev])
    showToast('Product created successfully', 'success')
  }

  // Handle product updated
  const handleProductUpdated = (updatedProduct) => {
    console.log('✅ Product updated:', updatedProduct)
    // Enrich the updated product with category and unit details
    const enrichedProduct = {
      ...updatedProduct,
      categoryDetails: categories.find(cat => cat._id === updatedProduct.category) || null,
      unitDetails: units.find(unit => unit._id === updatedProduct.unit) || null
    }
    setProducts(prev => prev.map(product => 
      product._id === updatedProduct._id ? enrichedProduct : product
    ))
    showToast('Product updated successfully', 'success')
  }

  // Handle product deleted
  const handleProductDeleted = (deletedProduct) => {
    console.log('✅ Product deleted:', deletedProduct)
    setProducts(prev => prev.filter(product => product._id !== deletedProduct._id))
    showToast('Product deleted successfully', 'success')
  }

  // Handle product disabled
  const handleProductDisabled = (disabledProduct) => {
    console.log('✅ Product disabled:', disabledProduct)
    setProducts(prev => prev.map(product => 
      product._id === disabledProduct._id ? disabledProduct : product
    ))
    showToast('Product disabled successfully', 'success')
  }

  // Simple refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setLoading(true)
    
    try {
      // Fetch fresh data
      const [categoriesResponse, unitsResponse] = await Promise.all([
        CategoryService.getAllCategories(),
        UnitsService.getAllUnits()
      ])
      
      const categoriesData = categoriesResponse?.data?.categories || []
      const unitsData = unitsResponse?.data?.units || []
      
      console.log('🔄 Refresh - Categories:', categoriesData.length)
      console.log('🔄 Refresh - Units:', unitsData.length)
      
      setCategories(categoriesData)
      setUnits(unitsData)
      
      // Fetch products with fresh data
      await fetchProducts(categoriesData, unitsData)
      
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Get status badge classes
  const getStatusBadgeClasses = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }

  // Get type badge classes
  const getTypeBadgeClasses = (type) => {
    return type === 'product' 
      ? 'bg-blue-100 text-blue-800'
      : 'bg-orange-100 text-orange-800'
  }

  // Show unauthorized access message if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <ShieldAlert className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              You don't have permission to access the All Products page. This page is only available to Admin users.
            </p>
            <p className="text-xs text-gray-400">
              Current Role: <span className="font-medium text-gray-600">{userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1).replace('_', ' ') : 'Unknown'}</span>
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-md w-full ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white p-4 rounded-lg shadow-lg`}>
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Products</h1>
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={loading}
            className="bg-white border-gray-300"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="bg-white">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Products</h2>
            </div>
            {(searchTerm || categoryFilter || typeFilter || statusFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <select
                value={categoryFilter}
                onChange={handleCategoryFilterChange}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="">All Categories</option>
                {categories.filter(cat => cat.isActive).map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="">All Types</option>
                {productTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products List */}
      <Card>
        <CardContent className="p-0">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Products List ({filteredProducts.length})
              </h3>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <InlineLoader title="Loading products..." />
          )}

          {/* Empty State */}
          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {products.length === 0 ? 'No products found' : 'No products match your filters'}
              </h3>
              <p className="text-gray-500 mb-4">
                {products.length === 0 
                  ? 'Get started by creating your first product.' 
                  : 'Try adjusting your search criteria.'
                }
              </p>
              {products.length === 0 && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              )}
            </div>
          )}

          {/* Products Table */}
          {!loading && filteredProducts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Outlet Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery Prices
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="pl-9 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <tr key={product._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.sku}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {product.categoryDetails?.name || 'Category not found'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClasses(product.type)}`}>
                          {product.type === 'product' ? '📦 Product' : '🍳 Recipe Based'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(product.outletPrice)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs space-y-1">
                          <div>Pick Me: {formatCurrency(product.pickMePrice)}</div>
                          <div>Uber: {formatCurrency(product.uberPrice)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeClasses(product.isActive)}`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {/* View Button */}
                          <button
                            onClick={() => handleViewProduct(product)}
                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                            title="View product"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="p-1 text-gray-400 hover:text-orange-600 rounded"
                            title="Edit product"
                          >
                            <Edit className="h-4 w-4" />
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                            title="Delete product"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onProductAdded={handleProductAdded}
        categories={categories}
        units={units}
      />

      <ViewProductModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        product={selectedProduct}
      />

      <EditProductModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        product={selectedProduct}
        onProductUpdated={handleProductUpdated}
        categories={categories}
        units={units}
      />

      <DeleteProductModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        product={selectedProduct}
        onProductDeleted={handleProductDeleted}
        onProductDisabled={handleProductDisabled}
      />

      {/* Click outside to close dropdown */}
      {actionMenuOpen && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={closeActionMenu}
        />
      )}
    </div>
  )
}

export default AllProducts